import { Messages } from 'config';
import { CashCardType } from 'types';
import { useAppSelector } from 'store';
import { mapQuery } from 'utils/query';
import { Flex } from 'components/flex';
import { List } from 'components/list';
import { formatPrice } from 'utils/helpers';
import { formatCalendarDate } from 'utils/date';
import { ListCard } from 'components/list-card';
import { ListHeader } from 'components/list-header';
import { useDeleteMutation } from 'store/api/payout';
import { useCashManagerQuery } from 'store/api/event';
import { DeleteDialog } from 'components/delete-dialog';
import { selectCanReadMissingSales } from 'store/slices/auth';
import { DownloadButton } from 'components/specified-buttons';
import { CashManagerExpenseAndPayout } from 'store/api/event/types';
import { CreateBonusAdvanceDialog } from 'components/create-bonus-advance-dialog';
import {
  isDesktop,
  useFilter,
  handleMutation,
  useSearchQuery,
  useDownloadFile,
} from 'hooks';

import { CashCard, CashCards } from '../cash-cards';

import { CashFlowProps } from './types';
import { paths, titles, template, footerTabletTemplate } from './config';

export const CashFlow = ({ event }: CashFlowProps) => {
  const canReadMissing = useAppSelector(selectCanReadMissingSales);
  const desktop = isDesktop();
  const { downloadFile } = useDownloadFile();
  const { debounceQuery, ...search } = useSearchQuery();

  const { data, ...props } = useCashManagerQuery(String(event.id), {
    skip: !event.id,
  });

  const [remove, mutation] = useDeleteMutation();
  handleMutation({
    ...mutation,
    successMessage: `Expense ${Messages.DELETED_SUCCESSFULLY}`,
  });

  const filteredValues = useFilter<CashManagerExpenseAndPayout>({
    paths,
    query: debounceQuery,
    data: data?.payoutsAndExpenses || [],
  });

  return (
    // Side by side only on desktop: below 1200px the cash cards take the full
    // width, so on an iPad a row left the lists 0px wide. Stack them instead.
    <Flex gap={16} flexDirection={desktop ? 'row' : 'column'}>
      <CashCards
        data={data}
        eventId={event.id}
        customTotalCash={data?.totalCollected}
      >
        {!!data?.refundAmount && (
          <CashCard
            title="Refund"
            type={CashCardType.Out}
            amount={data?.refundAmount}
          />
        )}
      </CashCards>
      <Flex gap={16} flexDirection="column" style={{ flex: 1, minWidth: 0 }}>
        {canReadMissing &&
          !!data?.entries?.some(
            (row) => row.sourceType === 'EventMissingSale'
          ) && (
            // Same insets as the cashflow table below: no inner padding and
            // an 8px header, so the rows' edges and the header's columns line
            // up with it (they sat 16px in from each edge).
            <List
              mainWrapperStyles={{ padding: 0 }}
              header={
                <ListHeader padding="8px" titles={['Date', 'Type', 'Amount']} />
              }
              count={
                data.entries.filter(
                  (row) => row.sourceType === 'EventMissingSale'
                ).length
              }
            >
              {data.entries
                .filter((row) => row.sourceType === 'EventMissingSale')
                .map((row) => (
                  <ListCard
                    key={`missing-${row.sourceId}`}
                    headers={['Date', 'Type', 'Amount']}
                    values={[
                      row.date,
                      row.type === 'Refund'
                        ? 'Missing-sale refund'
                        : 'Missing sale',
                      formatPrice(
                        row.type === 'Refund' ? -row.amount : row.amount
                      ),
                    ]}
                  />
                ))}
            </List>
          )}
        <List
          {...mapQuery(props)}
          {...search}
          count={filteredValues.length}
          mainWrapperStyles={{ padding: 0 }}
          searchContainerStyle={{ margin: '0 0 16px 0' }}
          header={
            <ListHeader padding="8px" titles={titles} template={template} />
          }
          searchBarRightSlot={
            <CreateBonusAdvanceDialog
              withReceipt
              isWideDialog
              eventId={event.id}
              isOnlyCashLocation
              showSelectExpensesType
              customTitle="Create cashflow"
            />
          }
          footer={
            <ListCard
              selected
              padding="16px"
              template={template}
              tabletTemplate={footerTabletTemplate}
              mobileTemplate={footerTabletTemplate}
              values={['Total', formatPrice(data?.totalOut)]}
            />
          }
        >
          {filteredValues.map((value) => (
            <ListCard
              key={value.id}
              headers={titles}
              template={template}
              values={[
                // Expense payment dates and payout item dates are calendar
                // days (stored at 00:00 UTC): never shift them a day back.
                formatCalendarDate(value?.date),
                formatPrice(value?.amount),
                value?.type,
                value?.employee,
                value?.notes,
                value?.category,
                <Flex gap={16} justifyContent="flex-end">
                  {value.receiptUrl && (
                    <DownloadButton
                      url={value.receiptUrl}
                      onDownloadFile={downloadFile}
                    />
                  )}

                  {value?.type !== 'Expense' && (
                    <DeleteDialog
                      id={value?.id}
                      action={remove}
                      title={`Delete ${value.type}`}
                      name={`${formatPrice(value?.amount)}`}
                    />
                  )}
                </Flex>,
              ]}
            />
          ))}
        </List>
      </Flex>
    </Flex>
  );
};
